/**
 * Quadro de efeitos sonoros (system/sfxPlayer; TfrmSFXPlayerModule): barra com
 * "adicionar" e o volume dos efeitos, e a lista de efeitos. Um clique do mestre
 * toca o efeito para a mesa inteira.
 */
import { useCallback, useRef, useState } from 'react';
import { useT } from '../../i18n/index.js';
import type { AudioTrack } from '../../shared.js';
import { useApp } from '../../store.js';
import { ContextMenu, type MenuAnchor } from '../../ui/ContextMenu.js';
import { Icon } from '../../ui/icons.js';
import { toast } from '../../ui/Toaster.js';
import { useLongPress } from '../../ui/touch.js';
import { unlockAudio, usePlayback } from './AudioEngine.js';
import { VolumeRow, askAudioLink, uploadAudioFiles, useIsRoomGm } from './MusicPlayerModule.js';
import './audio.css';

export function SfxPlayerModule() {
  const t = useT();
  const pads = useApp((s) => s.audio?.pads ?? EMPTY);
  const settings = useApp((s) => s.settings);
  const updateSettings = useApp((s) => s.updateSettings);
  const enqueue = useApp((s) => s.enqueueAudio);
  const remove = useApp((s) => s.removeAudio);
  const trigger = useApp((s) => s.triggerSfx);
  const lastSfx = useApp((s) => s.lastSfx);
  const blocked = usePlayback((s) => s.blocked);
  const isGm = useIsRoomGm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; pad: AudioTrack | null } | null>(null);
  const press = useLongPress();
  const closeMenu = useCallback(() => setMenu(null), []);

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const tracks = await uploadAudioFiles(files);
      if (!tracks.length) toast(t('audio.notAudio'), 'error');
      for (const tr of tracks) enqueue(tr.url, tr.title, 'sfx');
    } catch (error) {
      toast(t('audio.uploadFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      setBusy(false);
    }
  };

  const addLink = async () => {
    const tr = await askAudioLink(t);
    if (tr) enqueue(tr.url, tr.title, 'sfx');
  };

  return (
    <div
      className="rd-sfx"
      onDragOver={(e) => {
        if (isGm && e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!isGm) return;
        e.preventDefault();
        void addFiles(e.dataTransfer.files);
      }}
    >
      <div className="rd-sfx__bar">
        {isGm ? (
          <button
            type="button"
            className="rd-iconBtn rd-iconBtn--big"
            title={t('sfx.add')}
            aria-label={t('sfx.add')}
            disabled={busy}
            onClick={(e) => setMenu({ anchor: { x: e.clientX, y: e.clientY }, pad: null })}
          >
            {busy ? <span className="rd-spinner" /> : <Icon name="plus" size={22} />}
          </button>
        ) : null}
        <div className="rd-sfx__volume">
          <VolumeRow
            label={t('sfx.volume')}
            value={settings.sfxVolume}
            muted={settings.audioMuted}
            onMute={() => updateSettings({ audioMuted: !settings.audioMuted })}
            onChange={(v) => updateSettings({ sfxVolume: v, audioMuted: false })}
          />
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          multiple
          hidden
          onChange={(e) => {
            const files = e.target.files;
            void addFiles(files).finally(() => (e.target.value = ''));
          }}
        />
      </div>
      {blocked ? (
        <button type="button" className="rd-audioBanner" onClick={unlockAudio}>
          <Icon name="volume" size={16} /> {t('audio.blocked')}
        </button>
      ) : null}

      <div className="rd-sfx__pads" role="list">
        {pads.map((pad) => (
          <div key={pad.id} role="listitem" className="rd-sfx__item">
            <button
              type="button"
              className={`rd-sfx__pad${lastSfx?.title === pad.title && lastSfx.url === pad.url ? ' is-last' : ''}`}
              disabled={!isGm}
              title={isGm ? t('sfx.playFor') : pad.title}
              onClick={() => {
                unlockAudio();
                trigger(pad.id);
              }}
              {...(isGm ? press.bind((anchor) => setMenu({ anchor, pad })) : {})}
            >
              <Icon name="bolt" size={18} />
              <span>{pad.title}</span>
            </button>
          </div>
        ))}
        {pads.length === 0 ? <p className="rd-sfx__empty">{isGm ? t('sfx.empty.gm') : t('sfx.empty')}</p> : null}
      </div>
      {!isGm && pads.length ? <p className="rd-music__note">{t('sfx.gmOnly')}</p> : null}

      {menu ? (
        <ContextMenu
          anchor={menu.anchor}
          label={menu.pad?.title ?? t('sfx.add')}
          onClose={closeMenu}
          entries={
            menu.pad
              ? [
                  { kind: 'title', label: menu.pad.title },
                  { label: t('sfx.play'), onSelect: () => trigger(menu.pad!.id) },
                  { kind: 'separator' },
                  { label: t('audio.remove'), danger: true, onSelect: () => remove(menu.pad!.id) },
                ]
              : [
                  { label: t('audio.addFiles'), onSelect: () => fileRef.current?.click() },
                  { label: t('audio.addLink'), onSelect: () => void addLink() },
                ]
          }
        />
      ) : null}
    </div>
  );
}

const EMPTY: AudioTrack[] = [];
